import dompurify from 'dompurify';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import FadeIn from '@/components/animations/FadeIn';
import CourseMapD3React from '@/components/CourseMap/CourseMapD3React';
import Loader from '@/components/Loader';
import PublicNavbar from '@/components/Navbar/PublicNavbar';
import { Skeleton } from '@/components/ui/skeleton';
import GlobalContext from '@/context/GlobalContext';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetMapCourse } from '@/hooks/queries/useMapCourse';
import { useGetSiteSettings } from '@/hooks/queries/useSiteSettings';
import useOutsideClick from '@/hooks/useOutsideClick';
// import Resources from '@/components/Resources';
import { useTabLabels } from '@/hooks/useTabLabels';
import { useWindowSize } from '@/hooks/useWindowSize';
import type { DisciplineWithCourses } from '@/lib/types';
import { GlobalBodyStyle } from '@/Shared/StyledElements';
import { StyledH3, StyledH4, StyledP } from '@/Shared/Typography/typography';
import { getFormattedDate, sanitizeOptions } from '@/utils';
import MobileParentMap from './MobileParentMap';
import {
	StyledBG,
	StyledCloseIcon,
	StyledContent,
	StyledLegend,
	StyledPanel,
	StyledSidebar,
} from './ParentMapStyles';
import DescriptionTab from './sub-components/DescriptionTab';
import OutcomeTab from './sub-components/OutcomeTab';
import { Panel, Tabs } from './sub-components/ParentMapTabs';
import ResourcesTab from './sub-components/ResourcesTab';
import UnitFocusTab from './sub-components/UnitFocusTab';

const ParentMap = () => {
	const { divisions } = useContext(GlobalContext);
	const labels = useTabLabels();
	const [isOpen, setIsOpen] = useState(false);

	const [activePanel, setActivePanel] = useState(false);
	const [currentCourseId, setCurrentCourseId] = useState('');
	const ref = useRef<HTMLDivElement>(document.createElement('div'));
	const [activeSidebar, setActiveSidebar] = useState(true);
	const { data: siteSettings, isLoading: siteSettingsLoading } = useGetSiteSettings();
	const { data: disciplines, isLoading: disciplinesLoading } = useGetDisciplines();
	const settingsLoading = siteSettingsLoading || disciplinesLoading;
	const settings = useMemo(
		() => ({ meta: siteSettings, disciplines: disciplines ?? [] }),
		[siteSettings, disciplines]
	);

	const { isLoading: courseLoading, refetch, data } = useGetMapCourse(currentCourseId || null);

	const openPanel = useCallback(
		(id: string) => {
			setCurrentCourseId(id);
			setActivePanel(true);
			refetch();
		},
		[refetch]
	);

	useOutsideClick(ref, () => {
		if (isOpen) {
			setIsOpen(false);
		}
	});

	const closePanel = () => {
		setActivePanel(false);
	};

	const [width] = useWindowSize();
	const isMobile = width > 0 && width < 768;

	if (width <= 1200 && !activeSidebar) {
		setActiveSidebar(false);
	}

	useEffect(() => {
		const root = document.documentElement;
		if (settings?.meta?.primary_color) {
			root.style.setProperty('--org-color', settings.meta.primary_color);
		}
	}, [settings]);

	return (
		<FadeIn delay={0}>
			<>
				<GlobalBodyStyle />
				<PublicNavbar isSearchable={!isMobile} />
				<StyledBG />
				{settingsLoading && (
					<div className="flex flex-row pb-5 pt-8 h-full gap-4">
						<Skeleton className="h-full w-full max-w-[400px] ml-5" />
						<Skeleton className="h-full w-full mr-5" />
					</div>
				)}
				{!settingsLoading &&
					(isMobile ? (
						<MobileParentMap />
					) : (
						<StyledContent>
							<StyledSidebar>
								<div
									className="legend-mission"
									style={{ backgroundImage: `url(${settings?.meta?.banner})` }}
								>
									<div className="overlay" />
									{settings?.meta?.statement_title && (
										<StyledH4 mb="8px">{settings?.meta?.statement_title}</StyledH4>
									)}
									<StyledP
										dangerouslySetInnerHTML={{
											__html: dompurify.sanitize(`${settings?.meta?.statement ?? ''}`, {
												...sanitizeOptions,
											}),
										}}
									/>
								</div>
								<StyledLegend className="map-legend">
									<strong>Course Disciplines</strong>
									<div>
										<div className="discipline-wrap">
											{settings?.disciplines.map(
												(discipline: Pick<DisciplineWithCourses, 'id' | 'name' | 'color'>) => (
													<div className="legend-item" key={discipline.id}>
														<div
															className="color-indicator"
															style={{
																backgroundColor: discipline.color ?? '',
															}}
														/>
														<span>{discipline.name}</span>
													</div>
												)
											)}
										</div>
										<strong>Course Divisions</strong>
										<div className="divisions-wrap">
											{divisions?.map((d: any) => (
												<div className="legend-item" key={d.id}>
													<span className="letter-indicator">
														{d.name.split('')[0].toUpperCase()}S
													</span>
													<span>{d.name}</span>
												</div>
											))}
										</div>
									</div>
								</StyledLegend>
							</StyledSidebar>
							<CourseMapD3React isAdmin={false} passedEvent={openPanel} />
							{activePanel && (
								<FadeIn style={{ height: '100%' }}>
									<>
										<StyledPanel>
											<StyledCloseIcon
												top="10px"
												right="-48px"
												className="parent-course-panel-close-btn"
												passedEvent={closePanel}
											/>
											{courseLoading && (
												<Loader
													size="small"
													ringColor="var(--org-color)"
													color="var(--org-color)"
												/>
											)}

											{!courseLoading && (
												<FadeIn className="wrapper parent-map-course-panel">
													<>
														<div className="course-panel-header">
															<StyledH3 mb="var(--spacing-2)" className="course-title">
																{data?.course_name}
															</StyledH3>
															{data?.last_updated && (
																<time className="date" dateTime={data.last_updated}>
																	Last Updated {getFormattedDate(data.last_updated)}
																</time>
															)}
														</div>
														<Tabs selected={0}>
															<Panel title={labels.description}>
																<DescriptionTab
																	courseDescription={data?.course_description ?? ''}
																	courseExtras={data?.course_extras ?? ''}
																	coursePrereq={data?.course_prereq ?? ''}
																/>
															</Panel>
															<Panel title={labels.outcomes}>
																<OutcomeTab courseOutcome={data?.courses_outcomes} />
															</Panel>
															<Panel title={labels.resources}>
																<ResourcesTab
																	courseResource={data?.courses_resources}
																	courseResourceInfo={data?.course_resource_info ?? ''}
																/>
															</Panel>
															<Panel title={labels.units}>
																<UnitFocusTab courseUnit={data?.courses_focuses} />
															</Panel>
														</Tabs>
													</>
												</FadeIn>
											)}
										</StyledPanel>
									</>
								</FadeIn>
							)}
						</StyledContent>
					))}
			</>
		</FadeIn>
	);
};

export default ParentMap;
